export type TSearchEchoState = {
  lastValue: string;
  unacknowledgedSearches: readonly string[];
};

/**
 * Decides which incoming `searchConfig.searchValue` updates a search input should apply. A parent that stores the search
 * (state, URL) echoes each of the input's searches back, and when it renders slower than the user types, those echoes
 * arrive after newer keystrokes; applying them would overwrite what the user just typed. Echoes are expected in the order
 * the searches were sent, so an incoming value matching a sent search acknowledges it and every search sent before it.
 */
export class SearchEchoHelper {
  static createState(initialValue: string): TSearchEchoState {
    return { lastValue: initialValue, unacknowledgedSearches: [] };
  }

  /** Records a search the input is about to send; `shouldSearch` is `false` when the value is already the current one. */
  static recordSearch(
    state: TSearchEchoState,
    value: string,
    expectsEcho: boolean
  ): { state: TSearchEchoState; shouldSearch: boolean } {
    if (value === state.lastValue) return { state, shouldSearch: false };
    const unacknowledgedSearches = expectsEcho
      ? [...state.unacknowledgedSearches, value]
      : state.unacknowledgedSearches;
    return {
      state: { lastValue: value, unacknowledgedSearches },
      shouldSearch: true,
    };
  }

  /** `shouldApply` is `true` when an incoming value comes from outside the input (a reset, back navigation), not an echo. */
  static receiveIncoming(
    state: TSearchEchoState,
    value: string
  ): { state: TSearchEchoState; shouldApply: boolean } {
    const echoIndex = state.unacknowledgedSearches.indexOf(value);
    if (echoIndex !== -1) {
      return {
        state: {
          ...state,
          unacknowledgedSearches: state.unacknowledgedSearches.slice(
            echoIndex + 1
          ),
        },
        shouldApply: false,
      };
    }
    if (value === state.lastValue) return { state, shouldApply: false };
    return {
      state: SearchEchoHelper.createState(value),
      shouldApply: true,
    };
  }
}
